import { useEffect, useRef } from "react";
import { LineSeriesOption } from "echarts";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../../app/hooks";
import { tooltipMark } from "../../../shared/reportUtil";
import { WriterEssayDraftHistoryViewModel } from "../../../shared/progress/essayDraftHistory/essayDraftHistoryReportApiSlice";
import {
	getSeriesData,
	getXAxisFromMaxDraftCount,
	updateOptions,
} from "../../../shared/progress/essayDraftHistory/ChartUtil";

interface EssayScoresByDraftChartProps {
	isAllWriters: boolean;
	data: WriterEssayDraftHistoryViewModel[];
	isConstructedResponse: boolean;
}

export default function EssayScoresByDraftChart({
	data,
	isAllWriters,
	isConstructedResponse,
}: EssayScoresByDraftChartProps) {
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		legend: {
			show: true,
			padding: 15,
			itemGap: 20,
			textStyle: {
				color: foregroundColor,
			},
			top: 0,
		},
		grid: {
			containLabel: true,
			right: 40,
			left: 40,
			top: 60,
			bottom: 25,
		},
		xAxis: {
			type: "category",
			data: getXAxisFromMaxDraftCount(data),
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		yAxis: {
			type: "value",
			min: 0,
			max: isConstructedResponse ? 3 : 30,
			interval: isConstructedResponse ? 1 : 5,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: isAllWriters ? "Average Score" : "Draft Score",
			nameLocation: "middle",
			nameGap: 40,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					axisValue: string;
					dataIndex: number;
					color: string;
					seriesName: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;
				const label = `<div>${isAllWriters ? "Average Draft Score" : "Draft Score"}</div>`;

				const lineItem = (p: any) => {
					return p.value > -1
						? `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${p.value}</b></div>`
						: "";
				};

				const writerCountLineItem = (p: any) => {
					return isAllWriters
						? `<div>Number of Students: <b>${averagesDict[p.value]}</b></div>`
						: "";
				};

				const content = params.reduce(
					(a: string, v: any) => a + writerCountLineItem(v) + lineItem(v),
					label
				);

				return content;
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	const chartRef = useRef<ReactEcharts>(null);

	useEffect(() => {
		const options: EChartsOption = isAllWriters
			? updateOptionsAllWriters(
					initialEChartOptions,
					getSeriesDataAllWriters(data, foregroundColor)
				)
			: updateOptions(initialEChartOptions, getSeriesData(data, foregroundColor));

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options, true);
	}, [data, isAllWriters, foregroundColor, chartPalette]);

	return (
		<div className="mt-8 p-4 border-2 border-gray-200">
			{data.length > 0 ? (
				<ReactEcharts
					ref={chartRef}
					option={initialEChartOptions}
					className="w-full !h-96"
				/>
			) : (
				<div className="w-full h-96 border-2 border-gray-200 flex items-center justify-center bg-gray-100">
					<p>No scores to display</p>
				</div>
			)}
		</div>
	);
}

function getSeriesDataAllWriters(
	data: WriterEssayDraftHistoryViewModel[],
	foregroundColor: string
): LineSeriesOption {
	return {
		type: "line",
		name: "Class Average",
		connectNulls: true,
		data: getClassDraftAverages(data),
		label: {
			show: true,
			color: foregroundColor,
		},
		lineStyle: {
			type: "solid",
		},
		emphasis: {
			focus: "series",
		},
	};
}

function updateOptionsAllWriters(
	options: EChartsOption,
	seriesData: LineSeriesOption
): EChartsOption {
	options.series = seriesData;
	return options;
}

const averagesDict: Record<number, number> = {};

function getClassDraftAverages(data: WriterEssayDraftHistoryViewModel[]): number[] {
	const essays = data.flatMap((d) =>
		d.essays.map((essay) => ({ ...essay, writerId: d.writerId }))
	);
	const maxDraftCount = Math.max(...essays.map((e) => e.drafts.length));
	const draftAverages: number[] = [];

	for (let i = 0; i < maxDraftCount; i++) {
		let sum = 0;
		let count = 0;

		for (const essay of essays) {
			if (essay.drafts[i]) {
				sum += essay.drafts[i].draftScore;
				count++;
			}
		}

		const avg = Number((count > 0 ? sum / count : 0).toFixed(1));
		draftAverages.push(avg);
		averagesDict[avg] = count;
	}

	return draftAverages;
}
